import { type ExportOptionsInfo, useExport } from '@Hooks/Export';
import {
    Button,
    EnumFilterExpressionOperation,
    FilterExpression,
    FlexRow,
    Modal,
} from '@synqo/frontend-component-library';
import { type FC, useCallback, useMemo, useState } from 'react';
import { RangeFilterOption } from './RangeFilterOption';
import type { FilterOptionProps } from '@Modules/Exports/FilterOptionProps';
import { ApiExportFilterOptionType } from '@Model';

interface ExportOptionsProperties
    extends Pick<ExportOptionsInfo, 'currentExportOption' | 'setCurrentExportOption' | 'resourceBasePath'> {}

const exportOptionFilterOptionComponent: { [key: number]: FC<FilterOptionProps<any>> } = Object.freeze({
    [ApiExportFilterOptionType.Range]: RangeFilterOption,
});

export function ExportOptionsModal({
    currentExportOption,
    setCurrentExportOption,
    resourceBasePath,
}: ExportOptionsProperties) {
    const onClose = useCallback(() => {
        setCurrentExportOption(undefined);
    }, [setCurrentExportOption]);

    const { download, isDownloading } = useExport(resourceBasePath);

    const [filterExpressionMap, setFilterExpressionMap] = useState<{ [key: string]: FilterExpression | null }>(
        Object.fromEntries(currentExportOption?.filter_options.map((filterOption) => [filterOption.field, null]) || []),
    );
    const [isValidFilterExpression, setIsValidFilterExpression] = useState<{ [key: string]: boolean }>(() =>
        Object.fromEntries(currentExportOption?.filter_options.map((filterOption) => [filterOption.field, true]) || []),
    );

    const updateFilterExpression = (key: string) => (expression: FilterExpression) => {
        setFilterExpressionMap({ ...filterExpressionMap, [key]: expression });
    };

    const updateFilterExpressionIsValid = (key: string) => (isValid: boolean) => {
        setIsValidFilterExpression({ ...isValidFilterExpression, [key]: isValid });
    };

    const filterIsNotValid = useMemo(
        () => Object.values(isValidFilterExpression).some((isValid) => !isValid),
        [isValidFilterExpression],
    );

    const getFilterExpression = (): FilterExpression | undefined => {
        const filterExpressions = Object.values(filterExpressionMap).filter((expression) => !!expression);
        if (filterExpressions.length === 1) {
            return filterExpressions[0];
        }
        if (filterExpressions.length) {
            return {
                operation: EnumFilterExpressionOperation.And,
                filters: filterExpressions,
            };
        }
        return undefined;
    };

    if (currentExportOption === undefined) {
        return undefined;
    }

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={currentExportOption.display_string}
            footer={
                <FlexRow justify={'center'} className={'pt-5'}>
                    {currentExportOption.formats.map((format) => (
                        <Button
                            key={`btnDownload-${currentExportOption.id}-${format.id}`}
                            onClick={async () => {
                                await download({
                                    exportOption: currentExportOption.id,
                                    format: format.id,
                                    filter: getFilterExpression(),
                                });
                                onClose();
                            }}
                            locked={isDownloading || filterIsNotValid}
                        >
                            {isDownloading ? 'Export wird vorbereitet' : `${format.display_string}-Datei exportieren`}
                        </Button>
                    ))}
                </FlexRow>
            }
        >
            <FlexRow>
                {!currentExportOption.filter_options && 'Dieser Export hat keine Konfigurationsmöglichkeiten'}
                {currentExportOption.filter_options.map((filterOption) => {
                    const Component = exportOptionFilterOptionComponent[filterOption.type];
                    if (!Component) {
                        return;
                    }
                    return (
                        <Component
                            key={filterOption.field}
                            filterOption={filterOption}
                            setFilterExpression={updateFilterExpression(filterOption.field)}
                            setIsValidFilterExpression={updateFilterExpressionIsValid(filterOption.field)}
                        />
                    );
                })}
            </FlexRow>
        </Modal>
    );
}
